import React from 'react'
import { Request, Response } from 'express'
import createBrowserContext from './createBrowserContext'
import createServerContext from './createServerContext'

/**
 * The CookieProvider exposes browser cookies (for client-side renders) and request cookies (for SSR) via a unified API.
 */
export type CookieGetter = (cookieName: string) => string | undefined
export type CookieSetter = (
  cookieName: string,
  cookieValue: string | object,
  cookieOptions?: CookieOptions
) => boolean
export type CookieContext = {
  get: CookieGetter
  set: CookieSetter
} | null

export type CookieOptions = {
  expires?: number | Date
  path?: string
  domain?: string
  secure?: boolean
  httpOnly?: boolean
}

export type CookieEnvironment = Window | { req: Request; res: Response }

// Create the underlying provider and consumer
const { Provider, Consumer } = React.createContext<CookieContext>(null)

// A wrapper provider acts as the state container and manager
interface CookieProviderProps {
  environment: CookieEnvironment
}

interface CookieProviderState {
  context: CookieContext
}

class CookieProvider extends React.Component<CookieProviderProps, CookieProviderState> {
  public constructor(props: CookieProviderProps) {
    super(props)

    this.state = {
      context: this.createContext(this.props.environment)
    }
  }

  public componentDidUpdate(prevProps: CookieProviderProps) {
    if (prevProps.environment !== this.props.environment) {
      this.context = this.createContext(this.props.environment)
    }
  }

  public createContext(environment: CookieEnvironment): CookieContext {
    if (environment && 'req' in environment) {
      return createServerContext(environment)
    } else {
      return createBrowserContext(environment)
    }
  }

  public render() {
    return <Provider value={this.state.context}>{this.props.children}</Provider>
  }
}

// Export with clean names
export { CookieProvider, Consumer as CookieConsumer }
